<script setup>
// Composant Callout du Weave DS — https://www.figma.com/design/v5HcECNGbCYRV7RQ09H1ln/Design-System?node-id=4342-5008

import { ref, computed } from 'vue'

const props = defineProps({
    intent: {
        type: String,
        required: true,
        validator: v => ['warning', 'danger', 'success', 'info'].includes(v),
    },
    title: { type: String, default: null },
    description: { type: String, default: null },
    icon: { type: String, default: null },
    dismissible: { type: Boolean, default: false },
    // Avec une clé, la fermeture est mémorisée : le callout ne réapparaît plus.
    storageKey: { type: String, default: null },
    actionLabel: { type: String, default: null },
    actionHref: { type: String, default: null },
})

const emit = defineEmits(['close'])

function wasDismissed() {
    if (!props.storageKey) return false

    try {
        return window.localStorage.getItem(`ui-callout:${props.storageKey}`) === 'dismissed'
    } catch {
        // localStorage indisponible : on affiche le callout.
        return false
    }
}

const visible = ref(!wasDismissed())
const hasAction = computed(() => !!props.actionLabel && !!props.actionHref)
const displayLabel = computed(() => props.actionLabel || 'En savoir plus')

function dismiss() {
    visible.value = false

    if (props.storageKey) {
        try {
            window.localStorage.setItem(`ui-callout:${props.storageKey}`, 'dismissed')
        } catch {
            /* localStorage indisponible : la fermeture ne sera pas mémorisée */
        }
    }

    emit('close')
}
</script>

<template>
    <div v-if="visible" class="ui-callout" :class="`ui-callout--${intent}`" role="status">
        <span v-if="icon" class="ui-callout__icon material-symbols-outlined" aria-hidden="true">
            {{ icon }}
        </span>
        <div class="ui-callout__body">
            <h5 v-if="title" class="ui-callout__title">{{ title }}</h5>
            <p class="ui-callout__description">
                <slot>{{ description }}</slot>
            </p>
            <a v-if="hasAction" class="ui-callout__action" :href="actionHref" target="_blank" rel="noopener noreferrer">
                {{ displayLabel }}
            </a>
        </div>
        <button v-if="dismissible" type="button" class="ui-callout__close" aria-label="Fermer" @click="dismiss">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
    </div>
</template>
